/**
 * /zh-tw/cornell-notes-method — 康乃爾筆記法 reframe bridge→concept (zh-TW).
 *
 * Targets 康乃爾筆記法 (1.9k, KD 0). Distinctive hook: teach the method, then
 * pivot — good notes capture, but decisions need tracking.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwCornellNotes() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/cornell-notes-method"
      hreflangLocales={['zhtw']}
      documentTitle="康乃爾筆記法完整教學｜範本＋進階：把筆記變決策 · Draftlize"
      documentDescription="康乃爾筆記法的格式與範本一次學會，並進一步教你：好的筆記法之後，如何把「決策」結構化保存，而不只是漂亮的筆記。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 筆記法</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            康乃爾筆記法——<br />
            <em>好筆記的下一步是好決策。</em>
          </h1>
          <p className="lede">
            康乃爾筆記法把一頁分成三塊，強迫你在記錄之外還要「提問」和「總結」，是很好的學習與會議筆記法。但筆記法解決的是「怎麼把內容記下來」。在團隊與產品的場景裡，真正會過時的不是筆記本身，而是筆記裡的<em>決策</em>——它需要被追蹤，而不只是被寫下。這頁先教你康乃爾筆記法，再談下一步。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">康乃爾筆記法的格式。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>筆記欄（右大欄）</strong>——上課或開會時記下重點。</li>
            <li><strong>線索欄（左窄欄）</strong>——事後補上關鍵字、問題，用來自我測驗。</li>
            <li><strong>總結欄（底部）</strong>——用一兩句話總結整頁，逼自己消化。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            用在會議上也很有效。但會議的產物除了筆記，還有「決議」。整理會議記錄時，可以搭配 <a href="/zh-tw/meeting-minutes-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>AI 會議記錄</a> 的做法。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">記下來 vs 追蹤決策</span>
            <h2 className="serif">筆記法讓你「記住」，<em>決策需要被「追蹤」。</em></h2>
          </div>
          <p>好的筆記法和 Draftlize 解的是不同問題。一個幫你消化內容，一個讓決策在改變時不會靜靜過時。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>筆記法（康乃爾等）</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>解決</td><td>把內容記住、消化</td><td className="ours yes">讓決策保持最新</td></tr>
            <tr><td>形式</td><td className="partial">一頁一頁的筆記</td><td className="ours yes">互相連結的決策卡片</td></tr>
            <tr><td>決策被推翻時</td><td className="no">舊筆記還在，但沒人知道過時</td><td className="ours yes">依賴的內容自動 stale</td></tr>
            <tr><td>AI agent</td><td className="partial">讀純文字筆記</td><td className="ours yes">用 MCP 讀決策脈絡</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          再好的筆記法，也只是幫你「記住」。它不會在三週後告訴你，哪個當初記下的決策已經不算數。
        </blockquote>
        <cite>筆記記住內容，Draftlize 記住決策與它的影響範圍。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '康乃爾筆記法怎麼用？', a: '把頁面分成筆記欄、線索欄、總結欄：當下記重點，事後補關鍵字與問題，最後用一兩句總結。重點是強迫回顧與消化。' },
            { q: '適合會議嗎？', a: '很適合。不過會議還會產生「決議」——那部分需要被追蹤。可搭配 Draftlize 把決策變成會過時會提醒的卡片。' },
            { q: '數位工具能做康乃爾筆記嗎？', a: '可以，很多筆記軟體有版型。但筆記軟體存的是文字；決策的脈絡與影響範圍，交給 Draftlize 更合適。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">筆記記住內容，Draftlize 記住決策<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            繼續用你喜歡的筆記法。把會議與研究裡的決策交給 Draftlize——它們會互相連結、保持最新，改變時自動標出受影響的地方。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
